import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Room, Hotspot, HorrorEvent } from '../types';
import { ROOMS } from '../data/rooms';
import { ITEMS } from '../data/items';
import { useGameStore, useGameActions } from '../hooks/useGameStore';
import { useCustomizeMode, isCustomizeMode } from '../hooks/useCustomizeMode';
import { CustomizeMode } from './CustomizeMode';

const FADE_DURATION = 1000; // ms

const ROOM_PATTERNS: Record<string, React.CSSProperties> = {
  classroom: {
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  },
  hallway: {
    background: 'linear-gradient(90deg, #16213e 0%, #1a1a2e 50%, #16213e 100%)',
  },
  staffroom: {
    background: 'linear-gradient(180deg, #1a1a2e 0%, #2a1a1e 100%)',
  },
  infirmary: {
    background: 'linear-gradient(180deg, #1a2a1a 0%, #1a1a2e 100%)',
  },
  music: {
    background: 'linear-gradient(180deg, #2a1a2e 0%, #1a1a3e 100%)',
  },
  library: {
    background: 'linear-gradient(180deg, #1e2a1e 0%, #1a1e2a 100%)',
  },
  science: {
    background: 'linear-gradient(180deg, #1a2a2e 0%, #1a1a2e 100%)',
  },
  art: {
    background: 'linear-gradient(180deg, #2e1a2a 0%, #1a1a2e 100%)',
  },
  rooftop: {
    background: 'linear-gradient(180deg, #0a0a2e 0%, #1a1a3e 50%, #0a0a1e 100%)',
  },
};

export const RoomView: React.FC = () => {
  const state = useGameStore();
  const actions = useGameActions();
  const [hoveredHotspot, setHoveredHotspot] = useState<string | null>(null);
  const [descriptionText, setDescriptionText] = useState<string>('');
  const [showDescription, setShowDescription] = useState(false);
  const [firedEvents, setFiredEvents] = useState<Set<string>>(new Set());
  const [displayedRoom, setDisplayedRoom] = useState(state.currentRoom);
  const [fadeOpacity, setFadeOpacity] = useState(1);
  const isTransitioning = useRef(false);

  const room: Room = ROOMS[displayedRoom];
  const pattern = ROOM_PATTERNS[room.bgPattern] || ROOM_PATTERNS.hallway;

  // Customize mode
  const customize = useCustomizeMode(displayedRoom);

  // Merge original hotspots (with overrides) and custom hotspots
  const mergedHotspots = useMemo(() => {
    const base = customize.applyOverrides(room.hotspots);
    return [...base, ...customize.customHotspots];
  }, [room.hotspots, customize.applyOverrides, customize.customHotspots]);

  // Sound effects for room transitions
  const doorSound = useRef(new Audio('/sounds/ドアを開ける.mp3'));
  const walkSound = useRef(new Audio('/sounds/足音.mp3'));
  const lockedSound = useRef(new Audio('/sounds/開かないドア.mp3'));
  const lastClickedHotspot = useRef<string>('');

  const STAIR_HOTSPOTS = ['hw1_stairs_up', 'hw2_stairs_down', 'hw2_stairs_up', 'hw3_stairs_down'];

  // Sync SFX volume
  useEffect(() => {
    doorSound.current.volume = state.sfxVolume;
    walkSound.current.volume = state.sfxVolume;
    lockedSound.current.volume = state.sfxVolume;
  }, [state.sfxVolume]);

  // Fade transition on room change
  useEffect(() => {
    if (state.currentRoom === displayedRoom) return;

    const sound = STAIR_HOTSPOTS.includes(lastClickedHotspot.current) ? walkSound : doorSound;
    sound.current.currentTime = 0;
    sound.current.play().catch(() => {});

    isTransitioning.current = true;
    setShowDescription(false);
    setFadeOpacity(0); // fade out

    const timer = setTimeout(() => {
      setDisplayedRoom(state.currentRoom); // switch room content
      setFadeOpacity(1); // fade in
      isTransitioning.current = false;
    }, FADE_DURATION);

    return () => {
      clearTimeout(timer);
      isTransitioning.current = false;
    };
  }, [state.currentRoom, displayedRoom]);

  // Horror events
  useEffect(() => {
    if (!room.horrorEvents) return;

    const timers: number[] = [];

    room.horrorEvents.forEach((event: HorrorEvent) => {
      if (event.oneShot && firedEvents.has(event.id)) return;

      if (event.trigger === 'enter') {
        const timer = window.setTimeout(() => {
          triggerHorrorEvent(event);
        }, 500);
        timers.push(timer);
      } else if (event.trigger === 'time' && typeof event.triggerValue === 'number') {
        const timer = window.setTimeout(() => {
          triggerHorrorEvent(event);
        }, event.triggerValue);
        timers.push(timer);
      }
    });

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedRoom]);

  const triggerHorrorEvent = useCallback((event: HorrorEvent) => {
    setFiredEvents(prev => new Set(prev).add(event.id));

    if (event.type === 'text') {
      actions.addMessage(event.content);
      setDescriptionText(event.content);
      setShowDescription(true);
      setTimeout(() => setShowDescription(false), event.duration);
    } else if (event.type === 'screen_effect') {
      actions.showScreenEffect({
        type: event.content as any,
        duration: event.duration,
      });
    }
  }, [actions]);

  const handleHotspotClick = useCallback((hotspot: Hotspot) => {
    setShowDescription(false);

    // Don't count custom hotspots toward exploration rate
    if (!hotspot.id.startsWith('custom_')) {
      actions.interactHotspot(hotspot.id);
    }

    // Navigate to another room
    if (hotspot.targetRoom) {
      lastClickedHotspot.current = hotspot.id;
      // Skip key check if the target room is already unlocked
      const isTargetUnlocked = hotspot.targetRoom && state.unlockedRooms.includes(hotspot.targetRoom);
      if (hotspot.requiredItem && !isTargetUnlocked && !state.inventory.includes(hotspot.requiredItem)) {
        if (!STAIR_HOTSPOTS.includes(hotspot.id)) {
          lockedSound.current.currentTime = 0;
          lockedSound.current.play().catch(() => {});
        }
        const itemName = ITEMS[hotspot.requiredItem]?.name || '鍵';
        setDescriptionText(`${itemName}が必要だ。`);
        setShowDescription(true);
        setTimeout(() => setShowDescription(false), 2000);
        return;
      }
      // Check if the target room itself is locked (handled by moveToRoom)
      const targetRoom = ROOMS[hotspot.targetRoom];
      if (targetRoom?.requiredKey && !state.unlockedRooms.includes(hotspot.targetRoom) && !state.inventory.includes(targetRoom.requiredKey)) {
        if (!STAIR_HOTSPOTS.includes(hotspot.id)) {
          lockedSound.current.currentTime = 0;
          lockedSound.current.play().catch(() => {});
        }
      }
      actions.moveToRoom(hotspot.targetRoom);
      return;
    }

    // Open puzzle (check before itemReward so puzzleId+itemReward hotspots work correctly)
    if (hotspot.puzzleId) {
      if (state.solvedPuzzles.includes(hotspot.puzzleId)) {
        // Puzzle already solved — give item if not yet collected
        if (hotspot.itemReward && !state.inventory.includes(hotspot.itemReward)) {
          actions.addItem(hotspot.itemReward);
          const item = ITEMS[hotspot.itemReward];
          actions.addMessage(`${item.icon} ${item.name}を手に入れた。`);
          actions.showAcquiredItem(hotspot.itemReward);
          return;
        }
        setDescriptionText('もう解決済みだ。');
        setShowDescription(true);
        setTimeout(() => setShowDescription(false), 1500);
        return;
      }
      actions.openPuzzle(hotspot.puzzleId);
      return;
    }

    // Give item
    if (hotspot.itemReward && !state.inventory.includes(hotspot.itemReward)) {
      actions.addItem(hotspot.itemReward);
      const item = ITEMS[hotspot.itemReward];
      actions.addMessage(`${item.icon} ${item.name}を手に入れた。`);
      actions.showAcquiredItem(hotspot.itemReward);
      return;
    }

    // Already collected item
    if (hotspot.itemReward && state.inventory.includes(hotspot.itemReward)) {
      setDescriptionText('すでに調べた場所だ。もう何もない。');
      setShowDescription(true);
      setTimeout(() => setShowDescription(false), 1500);
      return;
    }

    // Show description
    const desc = state.interactedHotspots.includes(hotspot.id) && hotspot.interactedDescription
      ? hotspot.interactedDescription
      : hotspot.description;
    setDescriptionText(desc);
    setShowDescription(true);
  }, [actions, state.inventory, state.solvedPuzzles, state.interactedHotspots]);

  // Aspect ratio: 1376 x 768
  const ASPECT_RATIO = 1376 / 768;

  return (
    <div style={{
      position: 'relative',
      zIndex: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0a0a0a',
      overflow: 'hidden',
    }}>
      {/* Aspect-ratio container */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxHeight: '100%',
        aspectRatio: `${ASPECT_RATIO}`,
        maxWidth: `calc(100vh * ${ASPECT_RATIO})`,
        ...(room.bgImage ? {} : pattern),
        overflow: 'hidden',
        opacity: fadeOpacity,
        transition: `opacity ${FADE_DURATION}ms ease-in-out`,
        pointerEvents: isTransitioning.current ? 'none' : 'auto',
      }}>
        {/* Background image */}
        {room.bgImage && (
          <img
            src={room.bgImage}
            alt={room.name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Scan line effect */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px)',
          pointerEvents: 'none',
          zIndex: 10,
        }} />

        {/* Vignette */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%)',
          pointerEvents: 'none',
          zIndex: 10,
        }} />

        {/* Room name */}
        <div style={{
          position: 'absolute',
          top: '1rem',
          left: '1rem',
          zIndex: 20,
          color: '#888',
          fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
          fontFamily: '"Noto Serif JP", serif',
          letterSpacing: '0.2em',
          textShadow: '0 0 6px rgba(0,0,0,0.9)',
        }}>
          {room.name}
        </div>

        {/* Room description */}
        <div style={{
          position: 'absolute',
          top: '2.8rem',
          left: '1rem',
          right: '1rem',
          zIndex: 20,
          color: '#666',
          fontSize: 'clamp(0.85rem, 2vw, 1rem)',
          fontFamily: '"Noto Serif JP", serif',
          lineHeight: 1.6,
          textShadow: '0 0 6px rgba(0,0,0,0.9)',
        }}>
          {room.description}
        </div>

        {/* Hotspots */}
        {mergedHotspots.map((hotspot) => {
          const isHovered = hoveredHotspot === hotspot.id;
          const isLocked = hotspot.requiredItem && !state.inventory.includes(hotspot.requiredItem);
          const isPuzzleSolved = hotspot.puzzleId && state.solvedPuzzles.includes(hotspot.puzzleId);
          const hasItem = hotspot.itemReward && state.inventory.includes(hotspot.itemReward);
          const isCustom = hotspot.id.startsWith('custom_');

          return (
            <div
              key={hotspot.id}
              onClick={() => handleHotspotClick(hotspot)}
              onMouseEnter={() => setHoveredHotspot(hotspot.id)}
              onMouseLeave={() => setHoveredHotspot(null)}
              style={{
                position: 'absolute',
                left: `${hotspot.x}%`,
                top: `${hotspot.y}%`,
                width: `${hotspot.width}%`,
                height: `${hotspot.height}%`,
                border: isCustomizeMode
                  ? (isHovered
                    ? `1px solid ${isCustom ? 'rgba(68, 170, 255, 0.7)' : 'rgba(200, 180, 150, 0.6)'}`
                    : `1px solid ${isCustom ? 'rgba(68, 170, 255, 0.35)' : 'rgba(200, 180, 150, 0.15)'}`)
                  : '1px solid transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isCustomizeMode
                  ? (isHovered
                    ? isCustom ? 'rgba(68, 170, 255, 0.1)' : 'rgba(200, 180, 150, 0.1)'
                    : isCustom ? 'rgba(68, 170, 255, 0.03)' : 'rgba(200, 180, 150, 0.02)')
                  : 'transparent',
                transition: 'all 0.3s',
                zIndex: 15,
                opacity: hasItem || isPuzzleSolved ? 0.4 : 1,
              }}
            >
              <span style={{
                color: isLocked ? '#a55' : isHovered ? '#ddd' : '#999',
                fontSize: 'clamp(0.8rem, 2.5vw, 1.05rem)',
                textAlign: 'center',
                textShadow: '0 0 4px rgba(0,0,0,0.8)',
                fontFamily: '"Noto Serif JP", serif',
                letterSpacing: '0.1em',
                pointerEvents: 'none',
                opacity: isCustomizeMode ? 1 : 0,
              }}>
                {isLocked ? '🔒 ' : ''}{hotspot.label}
                {isPuzzleSolved ? ' ✓' : ''}
              </span>
            </div>
          );
        })}

        {/* Customize mode overlay */}
        {isCustomizeMode && (
          <CustomizeMode
            hotspots={mergedHotspots}
            onResize={customize.saveBounds}
            onAdd={customize.addCustomHotspot}
            onEdit={customize.updateCustomHotspot}
            onDelete={customize.deleteCustomHotspot}
            isDirty={customize.isDirty}
            onSave={customize.saveCurrentRoom}
          />
        )}

        {/* Description popup */}
        {showDescription && (
          <div
            onClick={() => setShowDescription(false)}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.95))',
              padding: '3rem 1.5rem 1.5rem',
              cursor: 'pointer',
            }}
          >
            <div style={{
              color: '#ccc',
              fontSize: 'clamp(1rem, 3vw, 1.2rem)',
              lineHeight: 2,
              fontFamily: '"Noto Serif JP", serif',
              whiteSpace: 'pre-line',
              maxHeight: '40vh',
              overflow: 'auto',
            }}>
              {descriptionText}
            </div>
            <p style={{
              color: '#555',
              fontSize: '0.85rem',
              marginTop: '1rem',
              textAlign: 'center',
            }}>
              タップで閉じる
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
